import { generateClient } from "@/utils/helper/aws-generateClient-singleton";
import { listSellerInfos, listUserInfos } from "@/graphql/queries";
import { SellerInfo, UserInfo } from "@/types/AppTypes";

const client = generateClient();

export type UserWithSellerInfos = (Omit<UserInfo, "id" | "user_id" | "updated_at" | "created_at"> & {role:string} & SellerInfo);

export type UserWithSellerInfosResult = {
  users: UserInfo[];
  sellerInfos: SellerInfo[];
  combinedUsers: UserWithSellerInfos[];
  pages?: UserWithSellerInfosResult[];
  nextToken?: string | null;
};

export const fetchPaginatedUserWithSellerInfos = async (
  filters: Record<string, any>,
  pageToken?: string,
  limit: number = 10
): Promise<UserWithSellerInfosResult> => {
  const sellerRes: any = await client.graphql({
    query: listSellerInfos,
    variables: {
      filter: filters,
      nextToken: pageToken,
      limit,
    },
  });

  const sellerInfos: SellerInfo[] = sellerRes.data.listSellerInfos.items || [];
  const nextToken = sellerRes.data.listSellerInfos.nextToken ?? null;

  if (sellerInfos.length === 0)
    return { users: [], sellerInfos: [], combinedUsers: [], nextToken: null, pages: [] };

  const userIds = sellerInfos.map((seller) => seller.user_id).filter(Boolean);

  let users: UserInfo[] = [];

  if (userIds.length > 0) {
    const usersRes: any = await client.graphql({
      query: listUserInfos,
      variables: {
        filter: {
          or: userIds.map((userId) => ({ user_id: { eq: userId } })),
        },
      },
    });

    users = usersRes.data.listUserInfos.items || [];
  }

  const combinedUsers = combinedUserWithSellerInfos({ users, sellerInfos });

  return {
    users,
    sellerInfos,
    nextToken,
    combinedUsers,
  };
};

const combinedUserWithSellerInfos = ({
  users,
  sellerInfos,
}: {
  users: UserInfo[];
  sellerInfos: SellerInfo[];
}) => {
  return sellerInfos.map((seller) => {
    const user = users.find((user) => user.user_id === seller.user_id);

    return {
      first_name: user?.first_name,
      last_name: user?.last_name,
      email: user?.email,
      has_roles: user?.has_roles,
      current_role: user?.current_role,
      role:"Distributor",
      ...seller,
    };
  });
};
